// Quests: light, local engagement goals that reward exploring Re:Vault. Progress lives
// in localStorage, so it rides along in the cloud backup and study sync for signed-in users
// with zero extra plumbing (two devices' counts merge by taking the larger one). Nothing here talks to the server.
//
// The design is deliberately small: a fixed list of quests, each a counter toward a
// goal, bumped by named events the app fires at natural moments (open a book, rate a
// title, customize a look...). Completing one awards points; points roll up into a level.

import { bump, totals } from "../shared/activity.js";
import { metric } from "../shared/metric.js";
import { broadcast } from "../shared/store.js";
import { seasonOpen } from "../shared/seasons.js";

const COUNTED = new Set(["book", "worksheet", "search", "forums", "favorite", "rate"]);

const LS = "revault:quests";

// event name -> the quests it advances (by id). One event can feed several quests.
const QUESTS = [
	{ id: "first-book", title: "Crack a book", desc: "Open something from the library.", event: "book", goal: 1, points: 10 },
	{ id: "bookworm", title: "Bookworm", desc: "Open 10 library items.", event: "book", goal: 10, points: 30 },
	{ id: "first-rate", title: "Critic", desc: "Give a title a star rating.", event: "rate", goal: 1, points: 10 },
	{ id: "rate-five", title: "Tough crowd", desc: "Rate five titles.", event: "rate", goal: 5, points: 25 },
	{ id: "tool-user", title: "Toolbox", desc: "Open a study tool.", event: "tool", goal: 1, points: 10 },
	{ id: "tool-three", title: "Handy", desc: "Use 3 study tools.", event: "tool", goal: 3, points: 20 },
	{ id: "worksheeter", title: "Get to work", desc: "Open a Worksheets app.", event: "worksheet", goal: 1, points: 10 },
	{ id: "customizer", title: "Make it yours", desc: "Apply a Look or change your theme.", event: "customize", goal: 1, points: 15 },
	{ id: "collector", title: "Collector", desc: "Star 3 favorites.", event: "favorite", goal: 3, points: 15 },
	{ id: "social", title: "Say hi", desc: "Visit the community forums.", event: "forums", goal: 1, points: 10, retired: true },
	{ id: "searcher", title: "Look it up", desc: "Search or open a site from the bar.", event: "search", goal: 1, points: 10 },
	{ id: "plan", title: "Plan ahead", desc: "Add 3 tasks to your to-do list.", event: "todo-add", goal: 3, points: 15 },
	// Seasonal collectibles (shared/seasons.js): earned only during their season, kept for good.
	// Afterwards one shows only to people who collected it. `icon` picks its badge (BADGES below).
	{ id: "spooky-2026", title: "Spooky Week 2026", desc: "Open a study tool during Spooky Week to collect it.", event: "tool", goal: 1, points: 10, season: "spooky-2026", icon: "pumpkin" },
	{ id: "winter-2026", title: "First Snow 2026", desc: "Open any study tool during the winter season.", event: "tool", goal: 1, points: 10, season: "winter-2026", icon: "snowflake" },
];
const seasonal = (q) => !!q.season;

// Study milestones (docs/HOME-2026-10.md, F5): repeatable tiers over the private study
// ledger totals. Stored in done as "m-<track>-<n>". Never public, never sent anywhere.
export const MILESTONES = [
	{ track: "days", label: "study days", one: "study day", tiers: [1, 5, 15, 30, 60, 100] },
	{ track: "fo", label: "focus sessions", one: "focus session", tiers: [1, 10, 50, 100] },
	{ track: "fc", label: "cards known", tiers: [25, 250, 1000] },
	{ track: "td", label: "tasks done", tiers: [10, 100, 500] },
	{ track: "b", label: "books opened", tiers: [10, 50, 200] },
	// The best school-day streak (shared/streak.js). It only ever goes up, so nothing here is lost.
	{ track: "streak", label: "school days in a row", tiers: [5, 15, 30, 60] },
];
export const TIER_POINTS = [10, 15, 20, 30, 40, 50];
const mKey = (track, n) => "m-" + track + "-" + n;
// Every tier: [{ key, title, points, n, track }]. "1 study day", "5 study days".
const TIERS = MILESTONES.flatMap((m) => m.tiers.map((n, i) => ({ key: mKey(m.track, n), title: n + " " + (n === 1 && m.one ? m.one : m.label), points: TIER_POINTS[i], n, track: m.track })));
// Tiers reached by these totals and not yet in done. Pure.
export function milestonesDue(tot, done) { return TIERS.filter((t) => (tot[t.track] | 0) >= t.n && !done[t.key]); }
const milestonePoints = (done) => TIERS.reduce((s, t) => s + (done[t.key] ? t.points : 0), 0);
// Totals plus the best streak, which lives in its own key.
function progress() {
	let best = 0;
	try { const s = JSON.parse(localStorage.getItem("revault:streak") || "null"); best = s && Number.isInteger(s.best) && s.best > 0 ? s.best : 0; } catch (e) { void e; }
	return Object.assign(totals(), { streak: best });
}
// Award any newly reached tiers (app only, so a toast shows once). quiet: no toast (boot).
// A streak milestone reached at night is awarded without a toast.
export function checkMilestones(quiet = false, now = new Date()) {
	const state = read();
	const due = milestonesDue(progress(), state.done);
	if (!due.length) { emit(); return []; } // repaint progress in an open Quests view
	for (const d of due) state.done[d.key] = Date.now();
	write(state);
	const last = due[due.length - 1];
	const night = now.getHours() >= 21 || now.getHours() < 7;
	if (!quiet && toastFn && !(night && last.track === "streak")) toastFn("Milestone: " + last.title + " (+" + last.points + ")");
	emit();
	return due;
}
// Remove milestone progress (Settings, Clear study stats). Quest progress stays.
export function clearMilestones() {
	const state = read();
	for (const k of Object.keys(state.done)) if (k.startsWith("m-")) delete state.done[k];
	write(state);
	emit();
}
// Study ledger counts for app-side events (counts only). Tools count themselves when they
// open (tools/main.js), however they were opened.
const LEDGER = { book: "b", worksheet: "t" };

const byEvent = {};
for (const q of QUESTS) (byEvent[q.event] = byEvent[q.event] || []).push(q);

const listeners = new Set();
let toastFn = null; // set by the app so completions can surface a toast

function read() {
	try { const j = JSON.parse(localStorage.getItem(LS) || "{}") || {}; const obj = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v : {}); return { counts: obj(j.counts), done: obj(j.done) }; }
	catch (e) { void e; return { counts: {}, done: {} }; }
}
// Same contract as store.js writeJSON: false (and "rv:save-failed") when the browser is full.
function put(state) {
	try { localStorage.setItem(LS, JSON.stringify(state)); return true; }
	catch (e) { try { window.dispatchEvent(new CustomEvent("rv:save-failed", { detail: { key: LS } })); } catch (e2) { void e2; } return false; }
}
// Told like any store.js write, so study sync hears about it.
function write(state) { const ok = put(state); if (ok) broadcast(LS); return ok; }

export function onQuestChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }
function emit() { for (const fn of listeners) { try { fn(); } catch (e) { void e; } } }
export function setQuestToast(fn) { toastFn = fn; }

// Fire an event; advance every quest that listens for it. `weight` lets a single action
// count once (default) - callers dedupe where "distinct" matters (e.g. rate uses a Set).
export function track(event, weight = 1) {
	if (COUNTED.has(event)) metric(event);
	if (LEDGER[event]) bump(LEDGER[event]);
	const qs = byEvent[event];
	if (!qs) return;
	const state = read();
	let changed = false;
	const said = []; // one toast for everything this event finished, so none hides another
	for (const q of qs) {
		if (state.done[q.id] || q.retired) continue; // retired quests can no longer be earned
		if (seasonal(q) && !seasonOpen(q.season)) continue;
		const next = Math.min(q.goal, (state.counts[q.id] || 0) + weight);
		if (next !== state.counts[q.id]) { state.counts[q.id] = next; changed = true; }
		if (next >= q.goal && !state.done[q.id]) {
			state.done[q.id] = Date.now();
			said.push((seasonal(q) ? "Collected: " : "Quest complete: ") + q.title + " (+" + q.points + ")");
		}
	}
	if (said.length && toastFn) toastFn(said.join(" · "));
	if (changed) { write(state); emit(); }
}

// Every way of opening a study tool ends in openInternal (Tools cards, recents, the command
// palette, Island chips, Home, Study Hall pins, the helper dock), which hands the address here.
// The Tools list itself ("/tools") isn't a tool.
export function trackOpen(url) {
	if (/^\/tools\/[a-z]+(?:[?#]|$)/.test(String(url))) track("tool");
}

// "Say hi" is retired: hidden unless already done (its points stay). A seasonal collectible
// shows during its week, and afterwards only once it's collected.
export function getQuests(now = Date.now()) {
	const state = read();
	return QUESTS.filter((q) => state.done[q.id] || (!q.retired && (!seasonal(q) || seasonOpen(q.season, now)))).map((q) => ({
		...q,
		count: Math.min(q.goal, state.counts[q.id] || 0),
		done: !!state.done[q.id],
	}));
}
export function totalPoints() {
	const state = read();
	return QUESTS.reduce((sum, q) => sum + (state.done[q.id] ? q.points : 0), 0) + milestonePoints(state.done);
}
export function questStats() {
	const qs = getQuests();
	const done = qs.filter((q) => q.done).length;
	const points = totalPoints();
	// A gentle level curve: level up roughly every 50 points.
	const level = Math.floor(points / 50) + 1;
	const intoLevel = points % 50;
	return { done, total: qs.length, points, level, intoLevel, toNext: 50 };
}

// --- the quests view -----------------------------------------------------------------
// Rendered into the folder container as a custom view (same as forums), so it inherits
// the active theme/Look.
function el(tag, cls, text) { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }

export function renderQuests(container) {
	const paint = () => draw(container);
	container.textContent = "";
	draw(container);
	// Repaint live as progress changes while the view is open.
	if (!container._questSub) container._questSub = onQuestChange(() => { if (document.body.contains(container)) draw(container); });
	return paint;
}

// Badges for seasonal collectibles, by the quest's `icon`.
const BADGES = {
	pumpkin: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 8c-1.7-1.3-4.7-1.3-6.5.3C3 10.5 3.2 15.4 5.4 18 6.8 19.6 9 20 12 20s5.2-.4 6.6-2c2.2-2.6 2.4-7.5-.1-9.7C16.7 6.7 13.7 6.7 12 8z"/><path d="M12 8V5.8c0-1 .6-1.8 1.6-2.3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
	snowflake: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 2v20M9 3.5l3 2 3-2M9 20.5l3-2 3 2"/><path transform="rotate(60 12 12)" d="M12 2v20M9 3.5l3 2 3-2M9 20.5l3-2 3 2"/><path transform="rotate(120 12 12)" d="M12 2v20M9 3.5l3 2 3-2M9 20.5l3-2 3 2"/></svg>',
};

function draw(container) {
	container.textContent = "";
	const wrap = el("div", "rv-quests");

	const s = questStats();
	const head = el("div", "rv-quests-head");
	const lvl = el("div", "rv-quests-level");
	lvl.append(el("div", "rv-quests-lvl-n", "Lv " + s.level), el("div", "rv-quests-lvl-l", s.points + " pts"));
	const bar = el("div", "rv-quests-xp");
	bar.setAttribute("aria-hidden", "true"); // the line above it says the same in words
	const fill = el("span"); fill.style.width = Math.round((s.intoLevel / s.toNext) * 100) + "%";
	bar.appendChild(fill);
	const barWrap = el("div", "rv-quests-xpwrap");
	barWrap.append(el("div", "rv-quests-title", "Quests"), el("div", "rv-quests-sub", s.done + " of " + s.total + " complete · " + (s.toNext - s.intoLevel) + " pts to level " + (s.level + 1)), bar);
	head.append(lvl, barWrap);
	wrap.appendChild(head);

	const list = el("div", "rv-quests-list");
	const qs = getQuests();
	for (const q of [...qs.filter(seasonal), ...qs.filter((x) => !seasonal(x))]) { // a collectible leads the list
		const card = el("div", "rv-quest" + (q.done ? " done" : "") + (seasonal(q) ? " rv-quest-seasonal" : ""));
		const check = el("div", "rv-quest-check");
		check.innerHTML = seasonal(q)
			? BADGES[q.icon] || ""
			: q.done
				? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="5,13 10,18 19,6"/></svg>'
				: "";
		const main = el("div", "rv-quest-main");
		const title = el("div", "rv-quest-title", q.title);
		if (seasonal(q)) title.append(el("span", "rv-quest-tag", q.done ? "Collected" : "Collectible"));
		main.append(title, el("div", "rv-quest-desc", q.desc));
		const prog = el("div", "rv-quest-prog");
		prog.setAttribute("aria-hidden", "true"); // the count below says it in words
		const pfill = el("span"); pfill.style.width = Math.round((q.count / q.goal) * 100) + "%";
		prog.appendChild(pfill);
		main.appendChild(prog);
		main.appendChild(el("div", "rv-quest-count", q.done ? "Done · +" + q.points : q.count + " / " + q.goal));
		card.append(check, main);
		list.appendChild(card);
	}
	wrap.appendChild(list);
	wrap.appendChild(drawMilestones());
	container.appendChild(wrap);
}

function drawMilestones() {
	const tot = progress(), done = read().done;
	const sec = el("div", "rv-miles");
	const mt = el("h3", "rv-miles-title", "Milestones");
	sec.append(mt, el("div", "rv-miles-sub", "Private to this device. They grow as you study."));
	for (const m of MILESTONES) {
		const tiers = TIERS.filter((t) => t.track === m.track);
		const next = tiers.find((t) => !done[t.key]) || tiers[tiers.length - 1];
		const have = Math.min(tot[m.track] | 0, next.n);
		const row = el("div", "rv-mile"), main = el("div", "rv-mile-main"), marks = el("div", "rv-mile-marks"), prog = el("div", "rv-quest-prog"), fill = el("span");
		prog.setAttribute("aria-hidden", "true");
		marks.setAttribute("aria-hidden", "true");
		for (const t of tiers) { const d = el("span", done[t.key] ? "on" : ""); d.title = t.title; marks.appendChild(d); }
		fill.style.width = Math.round((done[next.key] ? 1 : have / next.n) * 100) + "%";
		prog.appendChild(fill);
		main.append(el("div", "rv-mile-label", done[next.key] ? next.title + " · all reached" : have + " / " + next.title), prog);
		row.append(main, marks);
		sec.appendChild(row);
	}
	return sec;
}
